import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {Box} from '@mui/material'
import {
  PGButton,
  PGChip,
  PGTypography,
  PGTheme,
} from '@progynyglobal/pg-ui-library'
import dayjs from 'dayjs'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as ClockIcon} from '@progyny/assets/icons/consultation-row/clock.svg'
import {ReactComponent as LanguageIcon} from '@progyny/assets/icons/consultation-row/language.svg'
import {ReactComponent as VideoCallIcon} from '@progyny/assets/icons/consultation-row/video-call.svg'
import {
  formatAppointmentDayOfWeek,
  formatAppointmentTime,
  formatLanguage,
} from '@progyny/utils/appointmentHelpers'
import {getCancellationUrl} from '@progyny/utils/calcom'

export interface ConsultationRowProps {
  status: 'upcoming' | 'notAttended' | 'completed'
  sessionLabel?: string
  title?: string
  scheduledTime?: string | null
  language?: string | null
  programSlug?: string
  sessionNumber?: number
  canJoin?: boolean | null
  meetingLink?: string | null
  colors?: any
  appointment?: {
    id?: string | null
    calcomBookingUid?: string | null
    canReschedule?: boolean | null
  } | null
  consultationSlug?: string
  onCancelRequested?: (cancelUrl: string) => void
}

const ConsultationItem: React.FC<{
  icon: React.ReactNode
  label: string
}> = ({icon, label}) => {
  const {classes} = useItemStyles()

  return (
    <Box className={classes.itemRow}>
      {icon}
      <PGTypography variant='caption' className={classes.itemLabel}>
        {label}
      </PGTypography>
    </Box>
  )
}

const ConsultationRow: React.FC<ConsultationRowProps> = ({
  status,
  sessionLabel,
  title,
  scheduledTime,
  language,
  programSlug,
  sessionNumber,
  appointment,
  canJoin,
  meetingLink,
  consultationSlug,
  colors,
  onCancelRequested,
}) => {
  const {t, i18n} = useTranslation()

  const history = useHistory()

  const [dayjsLocale, setDayjsLocale] = useState('en')

  useEffect(() => {
    const locale = i18n.language.toLowerCase()
    const languagePart = locale.split('-')[0]

    import(
      /* webpackInclude: /\.js$/ */
      `dayjs/locale/${locale}`
    )
      .then(() => {
        setDayjsLocale(locale)
      })
      .catch(() =>
        import(
          /* webpackInclude: /\.js$/ */
          `dayjs/locale/${languagePart}`
        )
          .then(() => {
            setDayjsLocale(languagePart)
          })
          .catch(() => {})
      )
  }, [i18n.language])

  const statusProps = {
    completed: {
      label: t('consultations_program_session_completed'),
      colors: {backgroundColor: '#D1FAE5', textColor: '#065F46'},
    },
    upcoming: {
      label: t('consultations_program_session_upcoming'),
      colors: {backgroundColor: '#E7EDF9', textColor: '#07378F'},
    },
    notAttended: {
      label: t('consultations_program_session_not_attended'),
      colors: {backgroundColor: '#FFF3EC', textColor: '#E97E19'},
    },
    other: {
      label: null,
      colors: {backgroundColor: '#E7EDF9', textColor: '#666666'},
    },
  }

  const statusLabel = statusProps[status]?.label ?? statusProps.other.label
  const statusColors = statusProps[status]?.colors ?? statusProps.other.colors

  const {classes} = useStyles({
    dateBackground: colors?.primaryLight || '#e7edf9',
    statusBackgroundColor: statusColors.backgroundColor,
    statusTextColor: statusColors.textColor,
  })

  const scheduleLabel = scheduledTime
    ? `${t('consultations_program_session_description', {day: formatAppointmentDayOfWeek(scheduledTime, i18n.language), time: formatAppointmentTime(scheduledTime, i18n.language), timezone: dayjs(scheduledTime).locale(dayjsLocale).offsetName('short') ?? ''})}`
    : ''

  const handleRescheduleClick = () => {
    if (!appointment?.canReschedule || !appointment?.calcomBookingUid) {
      return
    }

    if (consultationSlug) {
      history.push(`/consultations/${consultationSlug}/onboarding/reschedule`)
      return
    }

    if (programSlug && sessionNumber) {
      history.push(
        `/consultations/program/${programSlug}/book?session=${sessionNumber}&rescheduleUid=${appointment.calcomBookingUid}`
      )
    }
  }

  const hasStarted: boolean =
    !!scheduledTime && new Date(scheduledTime) <= new Date()

  const handleJoinMeetingClick = () => {
    if (meetingLink) {
      window.open(meetingLink, '_blank')
    }
  }

  const cancellationUrl = appointment?.calcomBookingUid
    ? getCancellationUrl(appointment.calcomBookingUid)
    : null

  const handleCancelClick = () => {
    if (!cancellationUrl) {
      return
    }

    if (onCancelRequested) {
      onCancelRequested(cancellationUrl)
    }
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.dateBox}>
        <PGTypography
          variant='caption'
          sx={{color: PGTheme.common.textColor, fontSize: '12px'}}
        >
          {dayjs(scheduledTime).locale(dayjsLocale).format('MMM').toUpperCase()}
        </PGTypography>
        <PGTypography
          variant='header'
          sx={{
            color: PGTheme.common.textColor,
            lineHeight: '32px',
          }}
        >
          {dayjs(scheduledTime).locale(dayjsLocale).format('D')}
        </PGTypography>
      </Box>

      <Box className={classes.middleColumn}>
        <Box className={classes.statusRow}>
          {statusLabel && (
            <PGChip
              label={statusLabel}
              size='small'
              className={classes.statusChip}
            />
          )}
          {sessionLabel && (
            <PGTypography
              variant='caption'
              sx={{color: '#6B7280', fontSize: '12px'}}
            >
              {sessionLabel}
            </PGTypography>
          )}
        </Box>

        {title && <PGTypography variant='label'>{title}</PGTypography>}

        <Box className={classes.detailsRow}>
          {scheduleLabel && (
            <ConsultationItem
              icon={<ClockIcon width={14} height={14} />}
              label={scheduleLabel}
            />
          )}
          <ConsultationItem
            icon={<VideoCallIcon width={14} height={14} />}
            label={t('consultations_program_session_microsoft')}
          />
          {language && (
            <ConsultationItem
              icon={<LanguageIcon width={11} height={11} />}
              label={formatLanguage(language)}
            />
          )}
        </Box>
      </Box>

      {status === 'upcoming' && (
        <Box className={classes.actionsRow}>
          <PGButton
            variant='text'
            label={t('common_cancel')}
            onClick={handleCancelClick}
            disabled={!cancellationUrl || hasStarted}
            size='medium'
          />
          <PGButton
            variant='outlined'
            label={t('consultations_program_fertility_session_reschedule')}
            onClick={handleRescheduleClick}
            disabled={
              !appointment?.canReschedule ||
              !appointment?.calcomBookingUid ||
              hasStarted
            }
            size='medium'
          />
          <PGButton
            variant='outlined'
            label={t('consultations_program_fertility_session_join_meeting')}
            onClick={handleJoinMeetingClick}
            disabled={!canJoin || !meetingLink}
            size='medium'
          />
        </Box>
      )}
    </Box>
  )
}

const useItemStyles = makeStyles()(() => ({
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
  },
  itemLabel: {
    color: '#6B7280',
    fontWeight: 500,
    lineHeight: '20px',
  },
}))

const useStyles = makeStyles<{
  dateBackground: string
  statusBackgroundColor: string
  statusTextColor: string
}>()((theme, {dateBackground, statusBackgroundColor, statusTextColor}) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #EDEDED',
    borderRadius: '16px',
    gap: '20px',
    padding: '22px 24px',
  },
  dateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1px',
    flexShrink: 0,
    width: '62px',
    height: '62px',
    borderRadius: '12px',
    background: dateBackground,
  },
  middleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  statusChip: {
    backgroundColor: `${statusBackgroundColor} !important`,
    color: `${statusTextColor} !important`,
    fontWeight: 600,
    fontSize: '12px',
    padding: '3px',
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'column',
    gap: '14px',
  },
}))

export default ConsultationRow
